(() => {
  'use strict';

  const VERSION = '2026.08.20-premium-scroll-v6';
  const ROOT_SELECTOR = 'wi-testimonials-marquee, [data-wi-testimonials-marquee]';
  const DESKTOP_INPUT = window.matchMedia('(hover: hover) and (pointer: fine)');
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  class WiTestimonialsNativeScroller {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('[data-viewport]');
      this.track = root.querySelector('[data-track]');
      this.progress = root.querySelector('[data-progress]');
      this.cards = [...root.querySelectorAll('[data-card]')];

      if (!this.viewport || !this.track) return;

      this.abortController = new AbortController();
      this.resizeObserver = null;
      this.scrollRaf = 0;
      this.autoplayRaf = 0;
      this.momentumRaf = 0;
      this.pauseTimer = 0;
      this.scrollStateTimer = 0;
      this.lastScrollActivity = 0;
      this.lastAutoplayTime = 0;
      this.targetScroll = this.viewport.scrollLeft;
      this.maxScroll = 0;
      this.progressTravel = 0;
      this.autoplayFinished = false;
      this.userPaused = false;
      this.hoverPaused = false;
      this.isVisible = true;

      this.mouseDragging = false;
      this.mouseStartX = 0;
      this.mouseStartScroll = 0;
      this.mouseLastX = 0;
      this.mouseLastTime = 0;
      this.mouseVelocity = 0;

      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchMoved = false;
      this.suppressClickUntil = 0;

      this.debug = null;
      this.debugCounters = {
        touchstart: 0,
        touchmove: 0,
        touchend: 0,
        pointerdown: 0,
        scroll: 0
      };

      this.autoplay = root.dataset.autoplay === 'true';
      this.speed = Math.max(0, Number(root.dataset.speed) || 0);
      this.pauseAfterInteraction = Math.max(0, Number(root.dataset.pauseInteraction) || 0) * 1000;

      this.initialize();
    }

    initialize() {
      if (this.root.dataset.wiReady === 'true') return;

      this.root.dataset.wiReady = 'true';
      this.root.dataset.wiVersion = VERSION;
      this.root.classList.add('is-native-scroll');

      // Explicitly reset legacy inline styles from older carousel builds.
      this.track.style.removeProperty('transform');
      this.track.style.removeProperty('will-change');
      this.viewport.style.removeProperty('overflow');
      this.viewport.style.removeProperty('touch-action');
      this.viewport.style.removeProperty('scroll-behavior');

      this.bindEvents();
      this.observe();
      this.measure();
      this.startAutoplay();
      this.initializeDebugPanel();
    }

    bindEvents() {
      const signal = this.abortController.signal;

      this.viewport.addEventListener('scroll', () => this.handleScroll(), {
        passive: true,
        signal
      });

      // Touch handlers are passive and observational only. Native browser
      // scrolling remains fully in control on phones and touch tablets.
      this.viewport.addEventListener('touchstart', (event) => this.handleTouchStart(event), {
        passive: true,
        signal
      });
      this.viewport.addEventListener('touchmove', (event) => this.handleTouchMove(event), {
        passive: true,
        signal
      });
      this.viewport.addEventListener('touchend', () => this.handleTouchEnd(), {
        passive: true,
        signal
      });
      this.viewport.addEventListener('touchcancel', () => this.handleTouchEnd(), {
        passive: true,
        signal
      });

      // Desktop-only enhancements. No pointermove or preventDefault logic is
      // attached to touch input.
      this.viewport.addEventListener('wheel', (event) => this.handleWheel(event), {
        passive: false,
        signal
      });
      this.viewport.addEventListener('pointerdown', (event) => this.handlePointerDown(event), { signal });
      this.viewport.addEventListener('pointermove', (event) => this.handlePointerMove(event), { signal });
      this.viewport.addEventListener('pointerup', (event) => this.handlePointerUp(event), { signal });
      this.viewport.addEventListener('pointercancel', (event) => this.handlePointerUp(event), { signal });
      this.viewport.addEventListener('lostpointercapture', (event) => this.handlePointerUp(event), { signal });
      this.viewport.addEventListener('mouseenter', () => {
        if (DESKTOP_INPUT.matches) this.hoverPaused = true;
      }, { signal });
      this.viewport.addEventListener('mouseleave', () => {
        this.hoverPaused = false;
        if (this.mouseDragging) this.finishMouseDrag();
      }, { signal });
      this.viewport.addEventListener('dragstart', (event) => event.preventDefault(), { signal });

      this.root.addEventListener('click', (event) => this.handleClick(event), {
        capture: true,
        signal
      });
      this.root.addEventListener('keydown', (event) => this.handleKeydown(event), { signal });

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stopAutoplay();
        else {
          this.measure();
          this.startAutoplay();
        }
      }, { signal });

      window.addEventListener('pageshow', () => this.measure(), { passive: true, signal });
      window.addEventListener('resize', () => this.scheduleMeasure(), { passive: true, signal });
      window.addEventListener('orientationchange', () => this.scheduleMeasure(), { passive: true, signal });
      window.visualViewport?.addEventListener('resize', () => this.scheduleMeasure(), { passive: true, signal });

      document.addEventListener('shopify:block:select', (event) => {
        const card = event.target?.closest?.('[data-card]');
        if (!card || !this.root.contains(card)) return;
        this.scrollCardIntoView(card, false);
      }, { signal });

      document.addEventListener('shopify:section:load', (event) => {
        if (event.target?.contains?.(this.root)) this.scheduleMeasure();
      }, { signal });
    }

    observe() {
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
        this.resizeObserver.observe(this.viewport);
        this.resizeObserver.observe(this.track);
      }

      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver((entries) => {
          this.isVisible = entries[0]?.isIntersecting ?? true;
          if (this.isVisible) this.startAutoplay();
          else this.stopAutoplay();
        }, { threshold: 0.01 });
        this.intersectionObserver.observe(this.root);
      }

      if (document.fonts?.ready) {
        document.fonts.ready.then(() => this.scheduleMeasure()).catch(() => {});
      }

      this.root.querySelectorAll('img').forEach((image) => {
        if (!image.complete) {
          image.addEventListener('load', () => this.scheduleMeasure(), {
            once: true,
            signal: this.abortController.signal
          });
        }
      });
    }

    scheduleMeasure() {
      if (this.measureRaf) return;
      this.measureRaf = requestAnimationFrame(() => {
        this.measureRaf = 0;
        this.measure();
      });
    }

    measure() {
      this.maxScroll = Math.max(this.viewport.scrollWidth - this.viewport.clientWidth, 0);
      this.targetScroll = clamp(this.viewport.scrollLeft, 0, this.maxScroll);
      this.root.classList.toggle('has-overflow', this.maxScroll > 1);
      this.root.dataset.wiOverflow = String(Math.round(this.maxScroll));

      if (this.progress) {
        const progressTrack = this.progress.parentElement;
        this.progressTravel = progressTrack
          ? Math.max(progressTrack.clientWidth - this.progress.offsetWidth, 0)
          : 0;
      }

      if (this.viewport.scrollLeft < this.maxScroll - 1) {
        this.autoplayFinished = false;
      }

      this.updateProgress();
      this.updateDebugPanel();
    }

    handleScroll() {
      this.debugCounters.scroll += 1;
      this.lastScrollActivity = performance.now();
      this.root.classList.add('is-scrolling');

      if (!this.scrollStateTimer) {
        const settleScrollState = () => {
          const remaining = 120 - (performance.now() - this.lastScrollActivity);
          if (remaining <= 0) {
            this.root.classList.remove('is-scrolling');
            this.scrollStateTimer = 0;
            return;
          }
          this.scrollStateTimer = window.setTimeout(settleScrollState, remaining);
        };
        this.scrollStateTimer = window.setTimeout(settleScrollState, 120);
      }

      if (this.scrollRaf) return;
      this.scrollRaf = requestAnimationFrame(() => {
        this.scrollRaf = 0;
        this.targetScroll = this.viewport.scrollLeft;
        this.updateProgress();
        this.updateDebugPanel();
      });
    }

    handleTouchStart(event) {
      const touch = event.touches[0];
      if (!touch) return;

      this.debugCounters.touchstart += 1;
      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
      this.touchMoved = false;
      this.stopMomentum();
      this.pauseForInteraction();
      this.updateDebugPanel();
    }

    handleTouchMove(event) {
      const touch = event.touches[0];
      if (!touch) return;

      this.debugCounters.touchmove += 1;
      const deltaX = Math.abs(touch.clientX - this.touchStartX);
      const deltaY = Math.abs(touch.clientY - this.touchStartY);

      if (deltaX > 7 || deltaY > 7) {
        this.touchMoved = true;
      }

      this.updateDebugPanel();
    }

    handleTouchEnd() {
      this.debugCounters.touchend += 1;
      if (this.touchMoved) {
        this.suppressClickUntil = performance.now() + 450;
      }
      this.pauseForInteraction();
      this.updateDebugPanel();
    }

    handleWheel(event) {
      if (!DESKTOP_INPUT.matches || this.maxScroll <= 1) return;

      const absX = Math.abs(event.deltaX);
      const absY = Math.abs(event.deltaY);

      // Horizontal trackpad gestures already have high-quality browser inertia.
      // Leave them native instead of processing them a second time in JavaScript.
      if (absX > absY && absX > 0.5) {
        this.stopMomentum();
        this.pauseForInteraction();
        return;
      }

      if (absY < 0.5) return;

      let delta = event.deltaY;
      if (event.deltaMode === 1) delta *= 28;
      else if (event.deltaMode === 2) delta *= this.viewport.clientWidth * 0.85;

      const atStart = this.viewport.scrollLeft <= 0.5;
      const atEnd = this.viewport.scrollLeft >= this.maxScroll - 0.5;

      if ((delta < 0 && atStart) || (delta > 0 && atEnd)) return;

      event.preventDefault();
      this.stopMomentum();
      this.pauseForInteraction();

      // Apply the wheel delta immediately. Modern trackpads already emit
      // progressively smaller deltas, so adding another easing loop causes lag.
      const next = clamp(this.viewport.scrollLeft + delta, 0, this.maxScroll);
      this.viewport.scrollLeft = next;
      this.targetScroll = next;
    }

    startMomentum(initialVelocity) {
      this.stopMomentum();
      if (REDUCED_MOTION.matches || Math.abs(initialVelocity) < 0.04) return;

      let velocity = initialVelocity;
      let lastTime = performance.now();

      const tick = (timestamp) => {
        const elapsed = Math.min(Math.max(timestamp - lastTime, 1), 32);
        lastTime = timestamp;

        const current = this.viewport.scrollLeft;
        const next = clamp(current + velocity * elapsed, 0, this.maxScroll);
        this.viewport.scrollLeft = next;
        this.targetScroll = next;

        const friction = Math.pow(0.9, elapsed / 16.667);
        velocity *= friction;

        const hitBoundary = next <= 0.5 || next >= this.maxScroll - 0.5;
        if (Math.abs(velocity) < 0.018 || hitBoundary) {
          this.momentumRaf = 0;
          return;
        }

        this.momentumRaf = requestAnimationFrame(tick);
      };

      this.momentumRaf = requestAnimationFrame(tick);
    }

    stopMomentum() {
      if (this.momentumRaf) cancelAnimationFrame(this.momentumRaf);
      this.momentumRaf = 0;
    }

    handlePointerDown(event) {
      this.debugCounters.pointerdown += 1;
      if (!DESKTOP_INPUT.matches || event.pointerType !== 'mouse' || event.button !== 0 || this.maxScroll <= 1) {
        this.updateDebugPanel();
        return;
      }

      const interactive = event.target.closest('a, button, input, textarea, select');
      if (interactive) return;

      this.stopMomentum();
      this.mouseDragging = true;
      this.mouseStartX = event.clientX;
      this.mouseStartScroll = this.viewport.scrollLeft;
      this.mouseLastX = event.clientX;
      this.mouseLastTime = performance.now();
      this.mouseVelocity = 0;
      this.root.classList.add('is-dragging');
      this.pauseForInteraction();
      this.viewport.setPointerCapture?.(event.pointerId);
    }

    handlePointerMove(event) {
      if (!this.mouseDragging || event.pointerType !== 'mouse') return;

      const now = performance.now();
      const deltaX = event.clientX - this.mouseStartX;
      const nextScroll = clamp(this.mouseStartScroll - deltaX, 0, this.maxScroll);
      const elapsed = Math.max(now - this.mouseLastTime, 1);

      this.mouseVelocity = (this.mouseLastX - event.clientX) / elapsed;
      this.mouseLastX = event.clientX;
      this.mouseLastTime = now;
      this.viewport.scrollLeft = nextScroll;
      this.targetScroll = nextScroll;
      event.preventDefault();
    }

    handlePointerUp(event) {
      if (!this.mouseDragging || event.pointerType !== 'mouse') return;
      this.viewport.releasePointerCapture?.(event.pointerId);
      this.finishMouseDrag();
    }

    finishMouseDrag() {
      if (!this.mouseDragging) return;
      this.mouseDragging = false;
      this.root.classList.remove('is-dragging');
      this.suppressClickUntil = performance.now() + 350;

      if (!REDUCED_MOTION.matches && Math.abs(this.mouseVelocity) > 0.05) {
        this.startMomentum(this.mouseVelocity);
      }

      this.pauseForInteraction();
    }

    handleClick(event) {
      if (performance.now() < this.suppressClickUntil) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (DESKTOP_INPUT.matches) return;
      if (event.target.closest('a, button')) return;

      const card = event.target.closest('[data-card].has-back');
      if (!card || !this.root.contains(card)) return;

      this.cards.forEach((candidate) => {
        if (candidate !== card) candidate.classList.remove('is-flipped');
      });
      card.classList.toggle('is-flipped');
    }

    handleKeydown(event) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      if (!this.root.contains(document.activeElement)) return;

      const cardWidth = this.cards[0]?.getBoundingClientRect().width || this.viewport.clientWidth * 0.75;
      const gap = parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap) || 0;
      const step = cardWidth + gap;

      let next = this.viewport.scrollLeft;
      if (event.key === 'ArrowLeft') next -= step;
      if (event.key === 'ArrowRight') next += step;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = this.maxScroll;

      event.preventDefault();
      this.pauseForInteraction();
      this.viewport.scrollTo({
        left: clamp(next, 0, this.maxScroll),
        behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth'
      });
    }

    scrollCardIntoView(card, smooth = true) {
      const viewportRect = this.viewport.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      const leftOverflow = cardRect.left - viewportRect.left;
      const rightOverflow = cardRect.right - viewportRect.right;

      let next = this.viewport.scrollLeft;
      if (leftOverflow < 0) next += leftOverflow;
      else if (rightOverflow > 0) next += rightOverflow;
      else return;

      this.viewport.scrollTo({
        left: clamp(next, 0, this.maxScroll),
        behavior: smooth && !REDUCED_MOTION.matches ? 'smooth' : 'auto'
      });
    }

    pauseForInteraction() {
      this.userPaused = true;
      this.autoplayFinished = false;
      clearTimeout(this.pauseTimer);

      this.pauseTimer = window.setTimeout(() => {
        this.userPaused = false;
        this.lastAutoplayTime = 0;
        this.startAutoplay();
      }, this.pauseAfterInteraction);
    }

    startAutoplay() {
      if (
        this.autoplayRaf ||
        !this.autoplay ||
        this.speed <= 0 ||
        this.maxScroll <= 1 ||
        this.autoplayFinished ||
        REDUCED_MOTION.matches ||
        document.hidden ||
        !this.isVisible
      ) return;

      const tick = (timestamp) => {
        if (!this.autoplayRaf) return;

        const blocked = this.userPaused || this.hoverPaused || this.mouseDragging;
        if (!blocked) {
          if (!this.lastAutoplayTime) this.lastAutoplayTime = timestamp;
          const elapsed = Math.min(timestamp - this.lastAutoplayTime, 50);
          const next = Math.min(
            this.viewport.scrollLeft + (this.speed * elapsed) / 1000,
            this.maxScroll
          );

          this.viewport.scrollLeft = next;
          this.targetScroll = next;

          if (next >= this.maxScroll - 0.5) {
            this.autoplayFinished = true;
            this.stopAutoplay();
            return;
          }
        }

        this.lastAutoplayTime = timestamp;
        this.autoplayRaf = requestAnimationFrame(tick);
      };

      this.autoplayRaf = requestAnimationFrame(tick);
    }

    stopAutoplay() {
      if (this.autoplayRaf) cancelAnimationFrame(this.autoplayRaf);
      this.autoplayRaf = 0;
      this.lastAutoplayTime = 0;
    }

    updateProgress() {
      if (!this.progress) return;
      const ratio = this.maxScroll > 0 ? clamp(this.viewport.scrollLeft / this.maxScroll, 0, 1) : 0;
      this.progress.style.transform = `translate3d(${ratio * this.progressTravel}px, 0, 0)`;
    }

    initializeDebugPanel() {
      const params = new URLSearchParams(window.location.search);
      if (params.get('wi_swipe_debug') !== '1') return;

      const panel = document.createElement('aside');
      panel.setAttribute('data-wi-swipe-debug', '');
      panel.style.cssText = [
        'position:fixed',
        'z-index:2147483647',
        'left:8px',
        'right:8px',
        'bottom:8px',
        'max-height:48vh',
        'overflow:auto',
        'padding:10px',
        'border:1px solid #111',
        'border-radius:8px',
        'background:rgba(255,255,255,.97)',
        'color:#111',
        'font:12px/1.4 monospace',
        'box-shadow:0 8px 30px rgba(0,0,0,.25)',
        'white-space:pre-wrap'
      ].join(';');

      const controls = document.createElement('div');
      controls.style.cssText = 'display:flex;gap:8px;margin-bottom:8px;';

      const moveButton = document.createElement('button');
      moveButton.type = 'button';
      moveButton.textContent = 'Move +120px';
      moveButton.style.cssText = 'padding:6px 10px;border:1px solid #111;background:#fff;color:#111;border-radius:6px;';
      moveButton.addEventListener('click', () => {
        this.viewport.scrollLeft = clamp(this.viewport.scrollLeft + 120, 0, this.maxScroll);
        this.updateDebugPanel();
      });

      const closeButton = document.createElement('button');
      closeButton.type = 'button';
      closeButton.textContent = 'Close';
      closeButton.style.cssText = moveButton.style.cssText;
      closeButton.addEventListener('click', () => panel.remove());

      const output = document.createElement('div');
      output.setAttribute('data-output', '');
      controls.append(moveButton, closeButton);
      panel.append(controls, output);
      document.body.append(panel);
      this.debug = { panel, output };
      this.updateDebugPanel();
    }

    updateDebugPanel() {
      if (!this.debug?.output) return;

      const styles = getComputedStyle(this.viewport);
      const rect = this.viewport.getBoundingClientRect();
      const pointX = Math.max(0, Math.min(window.innerWidth - 1, rect.left + rect.width / 2));
      const pointY = Math.max(0, Math.min(window.innerHeight - 1, rect.top + Math.min(rect.height / 2, 140)));
      const topElement = document.elementFromPoint(pointX, pointY);

      this.debug.output.textContent = [
        `WI version: ${VERSION}`,
        `ready: ${this.root.dataset.wiReady}`,
        `viewport: ${this.viewport.clientWidth}px`,
        `scrollWidth: ${this.viewport.scrollWidth}px`,
        `maxScroll: ${this.maxScroll}px`,
        `scrollLeft: ${Math.round(this.viewport.scrollLeft)}px`,
        `overflowX: ${styles.overflowX}`,
        `overflowY: ${styles.overflowY}`,
        `touchAction: ${styles.touchAction}`,
        `pointerEvents: ${styles.pointerEvents}`,
        `topAtCenter: ${topElement?.tagName || 'none'}.${String(topElement?.className || '').slice(0, 80)}`,
        `touch events: start ${this.debugCounters.touchstart}, move ${this.debugCounters.touchmove}, end ${this.debugCounters.touchend}`,
        `pointerdown: ${this.debugCounters.pointerdown}`,
        `scroll events: ${this.debugCounters.scroll}`,
        `UA: ${navigator.userAgent}`
      ].join('\n');
    }

    destroy() {
      this.stopAutoplay();
      this.stopMomentum();
      if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
      if (this.measureRaf) cancelAnimationFrame(this.measureRaf);
      clearTimeout(this.pauseTimer);
      clearTimeout(this.scrollStateTimer);
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      this.abortController.abort();
      this.debug?.panel?.remove();
      delete this.root.dataset.wiReady;
    }
  }

  const instances = new WeakMap();

  const initialize = (scope = document) => {
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    roots.push(...scope.querySelectorAll?.(ROOT_SELECTOR) || []);

    roots.forEach((root) => {
      if (instances.has(root)) return;
      const instance = new WiTestimonialsNativeScroller(root);
      if (root.dataset.wiReady === 'true') instances.set(root, instance);
    });
  };

  const boot = () => initialize(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => {
      instances.get(root)?.destroy();
      instances.delete(root);
    });
  });
})();
